import React, { useState, useRef } from 'react';
import { Stack, Globe, Wrench, Cpu, ShieldCheck, Plus, ArrowsOutSimple, Check, CornersIn, CornersOut } from '@phosphor-icons/react';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';

const INITIAL_LAYERS = [
  {
    id: 'publica',
    title: 'PÚBLICA',
    subtitle: 'CAPA DE ACCESO Y APPS DE USUARIO',
    themeColor: 'emerald',
    badgeText: 'CAPA 1',
    bgStyle: 'bg-[#064e3b] bg-gradient-to-r from-[#065f46] via-[#047857] to-[#065f46]',
    borderColor: 'border-emerald-400/60',
    textColor: 'text-emerald-300',
    badgeBg: 'bg-emerald-950/80 text-emerald-300 border-emerald-400/50',
    icon: Globe,
    nodes: [
      { id: 'n1', title: 'Interfaz Principal (React)', type: 'FRONTEND', status: 'PROTOTIPO VISUAL', desc: 'Panel modular de interacción y visualización en React', x: 40, y: 80, isBubble: false }
    ]
  },
  {
    id: 'intermedia',
    title: 'INTERMEDIA',
    subtitle: 'CAPA OPERATIVA Y LABORATORIO',
    themeColor: 'amber',
    badgeText: 'CAPA 2',
    bgStyle: 'bg-[#78350f] bg-gradient-to-r from-[#854d0e] via-[#a16207] to-[#854d0e]',
    borderColor: 'border-amber-400/60',
    textColor: 'text-amber-300',
    badgeBg: 'bg-amber-950/80 text-amber-300 border-amber-400/50',
    icon: Wrench,
    nodes: [
      { id: 'n2', title: 'Backend REST / FastAPI', type: 'API', status: 'LOCAL API', desc: 'Servicio FastAPI de orquestación y sesiones en MongoDB', x: 40, y: 80, isBubble: false }
    ]
  },
  {
    id: 'agentica',
    title: 'AGÉNTICA',
    subtitle: 'CAPA DE AGENTES Y ORQUESTACIÓN',
    themeColor: 'orange',
    badgeText: 'CAPA 3',
    bgStyle: 'bg-[#7c2d12] bg-gradient-to-r from-[#9a3412] via-[#c2410c] to-[#9a3412]',
    borderColor: 'border-orange-400/60',
    textColor: 'text-orange-300',
    badgeBg: 'bg-orange-950/80 text-orange-300 border-orange-400/50',
    icon: Cpu,
    nodes: [
      { id: 'n3', title: 'Origin AI Connector', type: 'AGENTE DEMO', status: 'DEMO CONNECTED', desc: 'Canal demostrativo de instrucción y aclaración', x: 40, y: 90, isBubble: false },
      { id: 'n4', title: 'Production AI Connector', type: 'AGENTE DEMO', status: 'DEMO ROSTER', desc: 'Canal demostrativo de generación de código y validación', x: 380, y: 90, isBubble: false }
    ]
  },
  {
    id: 'organizacional',
    title: 'ORGANIZACIONAL',
    subtitle: 'CAPA DE GOBERNANZA Y CONTROL CENTRAL',
    themeColor: 'rose',
    badgeText: 'CAPA 4',
    bgStyle: 'bg-[#881337] bg-gradient-to-r from-[#9f1239] via-[#be123c] to-[#9f1239]',
    borderColor: 'border-rose-400/60',
    textColor: 'text-rose-300',
    badgeBg: 'bg-rose-950/80 text-rose-300 border-rose-400/50',
    icon: ShieldCheck,
    nodes: [
      { id: 'n5', title: 'Pre-Deployment Sandbox', type: 'GOBERNANZA REAL', status: 'APROBACIÓN REQUERIDA', desc: 'Ejecución aislada real y aprobación humana', x: 40, y: 80, isBubble: false }
    ]
  }
];

const ContextLayersWorkspace = () => {
  const [layers, setLayers] = useState(INITIAL_LAYERS);
  const [selectedLayerId, setSelectedLayerId] = useState('all');

  // Dragging state
  const [draggingNode, setDraggingNode] = useState(null);
  const canvasRef = useRef(null);

  const selectedLayer = layers.find((l) => l.id === selectedLayerId);

  // Toggle node between Bubble mode and Card Window mode
  const toggleBubbleMode = (layerId, nodeId) => {
    setLayers((prevLayers) =>
      prevLayers.map((layer) => {
        if (layer.id !== layerId) return layer;
        return {
          ...layer,
          nodes: layer.nodes.map((node) =>
            node.id === nodeId ? { ...node, isBubble: !node.isBubble } : node
          )
        };
      })
    );
  };

  // Add new dynamic node to active layer
  const handleAddNewNode = (targetLayerId) => {
    const newNodeId = `node_${Date.now()}`;
    const targetLayer = layers.find(l => l.id === targetLayerId);
    const newNode = {
      id: newNodeId,
      title: `Nuevo Nodo Visual (${targetLayer?.title || 'Capa'})`,
      type: 'DINÁMICO VISUAL',
      status: 'STATIC UI',
      desc: `Nodo visual flotante asignado a la capa ${targetLayer?.title || targetLayerId}`,
      x: 120 + Math.random() * 80,
      y: 100 + Math.random() * 60,
      isBubble: false
    };

    setLayers((prevLayers) =>
      prevLayers.map((layer) =>
        layer.id === targetLayerId
          ? { ...layer, nodes: [...layer.nodes, newNode] }
          : layer
      )
    );
  };

  // Drag handlers
  const handleMouseDown = (e, layerId, nodeId, currentX, currentY) => {
    e.stopPropagation();
    setDraggingNode({
      layerId,
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      initialX: currentX,
      initialY: currentY
    });
  };

  const handleMouseMove = (e) => {
    if (!draggingNode) return;
    const deltaX = e.clientX - draggingNode.startX;
    const deltaY = e.clientY - draggingNode.startY;

    const newX = Math.max(10, draggingNode.initialX + deltaX);
    const newY = Math.max(10, draggingNode.initialY + deltaY);

    setLayers((prevLayers) =>
      prevLayers.map((layer) => {
        if (layer.id !== draggingNode.layerId) return layer;
        return {
          ...layer,
          nodes: layer.nodes.map((node) =>
            node.id === draggingNode.nodeId ? { ...node, x: newX, y: newY } : node
          )
        };
      })
    );
  };

  const handleMouseUp = () => {
    setDraggingNode(null);
  };

  return (
    <div
      ref={canvasRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      className="h-full w-full bg-[#0A0C10] border border-[#1F2937] flex flex-col overflow-hidden select-none"
    >
      {/* Truth Notice Header Banner */}
      <div className="px-4 py-2 bg-[#13131A] border-b border-[#1F2937] flex items-center justify-between flex-wrap gap-2 shrink-0">
        <div className="flex items-center gap-3">
          <Stack size={18} className="text-[#00E5FF]" />
          <span className="text-xs terminal-font font-bold text-[#F3F4F6]">
            Lienzo Visual de Capas de Contexto
          </span>
          <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} label="STATIC / VISUAL MODEL" />
        </div>
        <p className="text-[11px] terminal-font text-[#9CA3AF]">
          Nodos visuales locales sin persistencia ni conexión remota a SystemAtlas, SetupOrion o ROOTMAP.
        </p>
      </div>

      {/* Layer Filter Toolbar */}
      <div className="p-3 border-b border-[#1F2937] bg-[#0A0C10] flex items-center justify-between flex-wrap gap-2 shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectedLayerId('all')}
            className={`px-3 py-1 text-xs terminal-font font-bold transition-all border ${
              selectedLayerId === 'all'
                ? 'bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/40'
                : 'bg-[#13131A] text-[#9CA3AF] border-[#1F2937] hover:text-[#F3F4F6]'
            }`}
          >
            VER TODAS LAS CAPAS (4)
          </button>
          {layers.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelectedLayerId(l.id)}
              className={`px-3 py-1 text-xs terminal-font font-bold uppercase transition-all border ${
                selectedLayerId === l.id
                  ? 'bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/40'
                  : 'bg-[#13131A] text-[#9CA3AF] border-[#1F2937] hover:text-[#F3F4F6]'
              }`}
            >
              {l.title} ({l.nodes.length})
            </button>
          ))}
        </div>

        <div className="text-[11px] terminal-font text-[#9CA3AF]">
          Instrucciones: Arrastra las ventanas o usa el botón de burbuja para flotar nodos.
        </div>
      </div>

      {/* Canvas Viewport */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 relative min-h-0">
        {(selectedLayerId === 'all' ? layers : [selectedLayer]).map((layer) => {
          if (!layer) return null;
          const IconComp = layer.icon;

          return (
            <div
              key={layer.id}
              className={`relative min-h-[220px] rounded-lg border ${layer.borderColor} ${layer.bgStyle} p-4 transition-all duration-300 shadow-xl overflow-hidden`}
            >
              {/* Layer Title & Add Button Header */}
              <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-3">
                <div className="flex items-center gap-3">
                  <IconComp className={`w-5 h-5 ${layer.textColor}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className={`text-sm font-bold terminal-font ${layer.textColor} tracking-wider`}>
                        {layer.title}
                      </h3>
                      <span className={`text-[10px] font-mono px-2 py-0.5 border ${layer.badgeBg}`}>
                        {layer.badgeText} ({layer.nodes.length} {layer.nodes.length === 1 ? 'nodo' : 'nodos'})
                      </span>
                    </div>
                    <p className="text-[10px] font-mono text-white/70 tracking-widest uppercase">
                      {layer.subtitle}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleAddNewNode(layer.id)}
                  className="px-2.5 py-1 bg-black/40 hover:bg-black/60 text-white border border-white/20 text-xs font-mono flex items-center gap-1 transition-all rounded"
                  title={`Añadir nodo visual a ${layer.title}`}
                >
                  <Plus size={12} /> Nodo en {layer.title}
                </button>
              </div>

              {/* Layer Node Canvas Area */}
              <div className="relative min-h-[160px] w-full">
                {layer.nodes.map((node) => (
                  <div
                    key={node.id}
                    onMouseDown={(e) => handleMouseDown(e, layer.id, node.id, node.x, node.y)}
                    style={{ left: `${node.x}px`, top: `${node.y}px` }}
                    className={`absolute z-20 cursor-grab active:cursor-grabbing transition-shadow ${
                      node.isBubble
                        ? 'p-2.5 rounded-full bg-slate-950/90 border-2 border-cyan-400 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.5)] flex items-center gap-2'
                        : 'w-72 bg-slate-950/95 border border-slate-700/80 text-slate-200 p-3 rounded-lg shadow-2xl'
                    }`}
                  >
                    {node.isBubble ? (
                      /* Bubble Mode Pill View */
                      <>
                        <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
                        <span className="text-xs font-mono font-bold whitespace-nowrap">{node.title}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleBubbleMode(layer.id, node.id); }}
                          className="p-1 hover:bg-cyan-500/20 text-cyan-300 rounded ml-1"
                          title="Restaurar a Ventana Completa"
                        >
                          <CornersOut size={14} />
                        </button>
                      </>
                    ) : (
                      /* Card Window View */
                      <>
                        <div className="flex items-center justify-between border-b border-slate-800 pb-1.5 mb-2">
                          <div className="flex items-center gap-2">
                            <ArrowsOutSimple size={12} className="text-cyan-400" />
                            <span className="text-xs font-mono font-bold text-slate-100">{node.title}</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleBubbleMode(layer.id, node.id); }}
                            className="p-1 hover:bg-slate-800 text-slate-400 hover:text-cyan-300 rounded transition-colors"
                            title="Convertir en Burbuja Flotante"
                          >
                            <CornersIn size={14} />
                          </button>
                        </div>

                        <div className="space-y-1.5 text-[11px] font-mono">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400">Tipo:</span>
                            <span className="text-cyan-300 font-semibold">{node.type}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400">Estado:</span>
                            <span className="text-emerald-400 font-semibold flex items-center gap-1">
                              <Check size={10} /> {node.status}
                            </span>
                          </div>
                          <p className="text-slate-300 text-[10px] leading-relaxed pt-1 border-t border-slate-900">
                            {node.desc}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dynamic Footer Summary Bar */}
      <div className="px-4 py-2 bg-[#13131A] border-t border-[#1F2937] flex items-center justify-between text-xs font-mono text-[#9CA3AF] shrink-0">
        {selectedLayerId === 'all' ? (
          <div>
            Total Nodos Visuales: <span className="text-[#00E5FF] font-bold">{layers.reduce((acc, l) => acc + l.nodes.length, 0)}</span> (PÚBLICA: {layers.find(l => l.id === 'publica')?.nodes.length || 0}, INTERMEDIA: {layers.find(l => l.id === 'intermedia')?.nodes.length || 0}, AGÉNTICA: {layers.find(l => l.id === 'agentica')?.nodes.length || 0}, ORGANIZACIONAL: {layers.find(l => l.id === 'organizacional')?.nodes.length || 0})
          </div>
        ) : (
          <div>
            Capa Enfocada: <span className="text-[#00E5FF] font-bold">{selectedLayer?.title}</span> ({selectedLayer?.badgeText}) — Nodos en esta capa: <span className="text-[#10B981] font-bold">{selectedLayer?.nodes.length}</span>
          </div>
        )}
        <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} size="sm" label="STATIC MODEL" />
      </div>
    </div>
  );
};

export default ContextLayersWorkspace;
